/**
 * Group Planning. Doc: docs/overall-plan/02-MANGO-PERSONAL/group-planning
 * Wired to GET/POST /api/v1/group-planning/plans.
 */
import React, { useEffect, useState } from 'react';
import { groupPlanningApi, type GroupPlan } from '../services/api';

const GroupPlanning: React.FC = () => {
  const [plans, setPlans] = useState<GroupPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);
  const [addItineraryPlanId, setAddItineraryPlanId] = useState<string | null>(null);
  const [addWishlistPlanId, setAddWishlistPlanId] = useState<string | null>(null);
  const [itineraryTitle, setItineraryTitle] = useState('');
  const [itineraryDesc, setItineraryDesc] = useState('');
  const [itineraryDateTime, setItineraryDateTime] = useState('');
  const [itineraryLocation, setItineraryLocation] = useState('');
  const [wishlistTitle, setWishlistTitle] = useState('');
  const [wishlistUrl, setWishlistUrl] = useState('');
  const [wishlistNotes, setWishlistNotes] = useState('');
  const [submittingItem, setSubmittingItem] = useState(false);

  const loadPlans = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await groupPlanningApi.getPlans();
      if (res?.success) setPlans(Array.isArray(res?.data) ? res.data : []);
      else setError(res?.error || 'Failed to load');
    } catch {
      setError('Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await groupPlanningApi.createPlan({ title: newTitle.trim() });
      if (res?.success) {
        setNewTitle('');
        setShowAdd(false);
        loadPlans(true);
      } else setError(res?.error || 'Failed to add plan');
    } catch {
      setError('Failed to add plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddItinerary = async (planId: string) => {
    if (!itineraryTitle.trim()) return;
    setSubmittingItem(true);
    setError(null);
    try {
      const res = await groupPlanningApi.addItineraryItem(planId, {
        title: itineraryTitle.trim(),
        description: itineraryDesc.trim() || undefined,
        dateTime: itineraryDateTime || undefined,
        location: itineraryLocation.trim() || undefined,
      });
      if (res?.success) {
        setItineraryTitle('');
        setItineraryDesc('');
        setItineraryDateTime('');
        setItineraryLocation('');
        setAddItineraryPlanId(null);
        loadPlans(true);
      } else setError(res?.error || 'Failed to add');
    } catch {
      setError('Failed to add');
    } finally {
      setSubmittingItem(false);
    }
  };

  const handleAddWishlist = async (planId: string) => {
    if (!wishlistTitle.trim()) return;
    setSubmittingItem(true);
    setError(null);
    try {
      const res = await groupPlanningApi.addWishlistItem(planId, {
        title: wishlistTitle.trim(),
        url: wishlistUrl.trim() || undefined,
        notes: wishlistNotes.trim() || undefined,
      });
      if (res?.success) {
        setWishlistTitle('');
        setWishlistUrl('');
        setWishlistNotes('');
        setAddWishlistPlanId(null);
        loadPlans(true);
      } else setError(res?.error || 'Failed to add');
    } catch {
      setError('Failed to add');
    } finally {
      setSubmittingItem(false);
    }
  };

  if (loading) return <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}><p>Loading…</p></div>;

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{ background: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)', borderRadius: '16px', padding: '24px', marginBottom: '24px', color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>📋 Group Planning</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>Plan events and trips with your group</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={() => setShowAdd((s) => !s)} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '8px', color: '#2d3748', cursor: 'pointer', fontSize: '14px' }}>Add Plan</button>
            <button type="button" onClick={() => loadPlans(true)} disabled={refreshing} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>
          </div>
        </div>
      </div>
      {showAdd && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
          <input type="text" placeholder="Plan title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleAdd} disabled={submitting || !newTitle.trim()} style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{submitting ? 'Adding…' : 'Create'}</button>
            <button type="button" onClick={() => { setShowAdd(false); setNewTitle(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
          </div>
        </div>
      )}
      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}
      {plans.length === 0 && !error && !showAdd && <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>No plans yet. Add a plan above.</div>}
      {plans.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {plans.map((p) => {
            const expanded = expandedPlanId === p.id;
            const showItineraryForm = addItineraryPlanId === p.id;
            const showWishlistForm = addWishlistPlanId === p.id;
            const itineraryLen = p.itinerary?.length ?? 0;
            const wishlistLen = p.wishlist?.length ?? 0;
            return (
              <li key={p.id} style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', background: '#fafafa', marginBottom: '8px', borderRadius: '12px' }}>
                <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }} onClick={() => setExpandedPlanId(expanded ? null : p.id)}>
                  <div>
                    <strong>{p.title}</strong>
                    {p.description && <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>{p.description}</p>}
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>{itineraryLen} item{itineraryLen !== 1 ? 's' : ''} · {wishlistLen} wishlist</p>
                  </div>
                  <span style={{ fontSize: '14px', color: '#64748b' }}>{expanded ? '−' : '+'}</span>
                </div>
                {expanded && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '14px' }}>Itinerary</strong>
                        {!showItineraryForm && <button type="button" onClick={(e) => { e.stopPropagation(); setAddItineraryPlanId(p.id); }} style={{ padding: '4px 10px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>+ Add</button>}
                      </div>
                      {p.itinerary && p.itinerary.length > 0 && (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: '8px' }}>
                          {p.itinerary.map((item) => (
                            <li key={item.id} style={{ padding: '6px 0', fontSize: '13px', borderBottom: '1px solid #e2e8f0' }}>{item.title}{item.location ? ` - ${item.location}` : ''}</li>
                          ))}
                        </ul>
                      )}
                      {showItineraryForm && (
                        <div style={{ background: '#fff', padding: '12px', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e2e8f0' }}>
                          <input type="text" placeholder="Item title" value={itineraryTitle} onChange={(e) => setItineraryTitle(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <input type="text" placeholder="Description (optional)" value={itineraryDesc} onChange={(e) => setItineraryDesc(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <input type="datetime-local" value={itineraryDateTime} onChange={(e) => setItineraryDateTime(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <input type="text" placeholder="Location (optional)" value={itineraryLocation} onChange={(e) => setItineraryLocation(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="button" onClick={() => handleAddItinerary(p.id)} disabled={submittingItem || !itineraryTitle.trim()} style={{ padding: '6px 12px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>{submittingItem ? 'Adding...' : 'Add'}</button>
                            <button type="button" onClick={() => { setAddItineraryPlanId(null); setItineraryTitle(''); setItineraryDesc(''); setItineraryDateTime(''); setItineraryLocation(''); }} style={{ padding: '6px 12px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '14px' }}>Wishlist</strong>
                        {!showWishlistForm && <button type="button" onClick={(e) => { e.stopPropagation(); setAddWishlistPlanId(p.id); }} style={{ padding: '4px 10px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>+ Add</button>}
                      </div>
                      {p.wishlist && p.wishlist.length > 0 && (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: '8px' }}>
                          {p.wishlist.map((item) => (
                            <li key={item.id} style={{ padding: '6px 0', fontSize: '13px', borderBottom: '1px solid #e2e8f0' }}>{item.title}</li>
                          ))}
                        </ul>
                      )}
                      {showWishlistForm && (
                        <div style={{ background: '#fff', padding: '12px', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e2e8f0' }}>
                          <input type="text" placeholder="Wishlist item title" value={wishlistTitle} onChange={(e) => setWishlistTitle(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <input type="url" placeholder="URL (optional)" value={wishlistUrl} onChange={(e) => setWishlistUrl(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <input type="text" placeholder="Notes (optional)" value={wishlistNotes} onChange={(e) => setWishlistNotes(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="button" onClick={() => handleAddWishlist(p.id)} disabled={submittingItem || !wishlistTitle.trim()} style={{ padding: '6px 12px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>{submittingItem ? 'Adding...' : 'Add'}</button>
                            <button type="button" onClick={() => { setAddWishlistPlanId(null); setWishlistTitle(''); setWishlistUrl(''); setWishlistNotes(''); }} style={{ padding: '6px 12px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default GroupPlanning;
